/**
 * Customer Portal (UI) - pure helpers for navigator graph shaping.
 */
import type { Brand, Campaign, PhoneNumber } from '../types/portal';

export type NavigatorGraphNode = {
  id: string;
  text: string;
  width?: number;
  height?: number;
};

export type NavigatorGraphEdge = {
  id: string;
  from: string;
  to: string;
};

export type NavigatorGraphInput = {
  brands: Brand[];
  campaigns: Campaign[];
  phoneNumbers: PhoneNumber[];
};

export type NavigatorGraphData = {
  nodes: NavigatorGraphNode[];
  edges: NavigatorGraphEdge[];
};

export type NavigatorNodeType = 'brand' | 'campaign' | 'phone';

export function navigatorNodeId(type: NavigatorNodeType, value: string): string {
  return `${type}:${value}`;
}

export function parseNavigatorNodeId(nodeId: string): { type: NavigatorNodeType; id: string } | null {
  const value = String(nodeId ?? '').trim();
  const separator = value.indexOf(':');
  if (separator <= 0 || separator === value.length - 1) return null;
  const type = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (type !== 'brand' && type !== 'campaign' && type !== 'phone') return null;
  return { type, id };
}

export function buildNavigatorGraph(input: NavigatorGraphInput): NavigatorGraphData {
  const brands = [...input.brands].sort((a, b) => {
    const nameCompare = String(a.name ?? a.id).localeCompare(String(b.name ?? b.id));
    if (nameCompare !== 0) return nameCompare;
    return String(a.id).localeCompare(String(b.id));
  });
  const campaigns = [...input.campaigns].sort((a, b) => {
    const brandCompare = String(a.brandId ?? '').localeCompare(String(b.brandId ?? ''));
    if (brandCompare !== 0) return brandCompare;
    const nameCompare = String(a.name ?? a.id).localeCompare(String(b.name ?? b.id));
    if (nameCompare !== 0) return nameCompare;
    return String(a.id).localeCompare(String(b.id));
  });
  const phoneNumbers = [...input.phoneNumbers].sort((a, b) => {
    const campaignCompare = String(a.campaignId ?? '').localeCompare(String(b.campaignId ?? ''));
    if (campaignCompare !== 0) return campaignCompare;
    const brandCompare = String(a.brandId ?? '').localeCompare(String(b.brandId ?? ''));
    if (brandCompare !== 0) return brandCompare;
    const labelCompare = String(a.label ?? a.id).localeCompare(String(b.label ?? b.id));
    if (labelCompare !== 0) return labelCompare;
    return String(a.id).localeCompare(String(b.id));
  });
  const nodes: NavigatorGraphNode[] = [];
  const edges: NavigatorGraphEdge[] = [];
  const seenNodeIds = new Set<string>();
  const seenEdgeIds = new Set<string>();

  const knownBrandIds = new Set<string>();
  const knownCampaignIds = new Set<string>();

  for (const brand of brands) {
    const brandNodeId = navigatorNodeId('brand', brand.id);
    knownBrandIds.add(brand.id);
    if (!seenNodeIds.has(brandNodeId)) {
      nodes.push({
        id: brandNodeId,
        text: `Brand: ${brand.name}`,
        width: 220,
        height: 56
      });
      seenNodeIds.add(brandNodeId);
    }
  }

  for (const campaign of campaigns) {
    const campaignNodeId = navigatorNodeId('campaign', campaign.id);
    knownCampaignIds.add(campaign.id);
    if (!seenNodeIds.has(campaignNodeId)) {
      nodes.push({
        id: campaignNodeId,
        text: `Campaign: ${campaign.name}`,
        width: 240,
        height: 56
      });
      seenNodeIds.add(campaignNodeId);
    }
    if (knownBrandIds.has(campaign.brandId)) {
      const parentNodeId = navigatorNodeId('brand', campaign.brandId);
      const edgeId = `${parentNodeId}->${campaignNodeId}`;
      if (!seenEdgeIds.has(edgeId)) {
        edges.push({
          id: edgeId,
          from: parentNodeId,
          to: campaignNodeId
        });
        seenEdgeIds.add(edgeId);
      }
    }
  }

  for (const phoneNumber of phoneNumbers) {
    const phoneNodeId = navigatorNodeId('phone', phoneNumber.id);
    if (!seenNodeIds.has(phoneNodeId)) {
      nodes.push({
        id: phoneNodeId,
        text: `${phoneNumber.label} (${phoneNumber.e164})`,
        width: 280,
        height: 56
      });
      seenNodeIds.add(phoneNodeId);
    }
    const parentNodeId = knownCampaignIds.has(phoneNumber.campaignId)
      ? navigatorNodeId('campaign', phoneNumber.campaignId)
      : knownBrandIds.has(phoneNumber.brandId)
        ? navigatorNodeId('brand', phoneNumber.brandId)
        : null;
    if (parentNodeId) {
      const edgeId = `${parentNodeId}->${phoneNodeId}`;
      if (!seenEdgeIds.has(edgeId)) {
        edges.push({
          id: edgeId,
          from: parentNodeId,
          to: phoneNodeId
        });
        seenEdgeIds.add(edgeId);
      }
    }
  }

  return { nodes, edges };
}
